// Availability — the pure rules behind the status dot, its tooltip and the
// "Set your status" payload. No React, no i18n: every human string is passed in,
// so the rules can be read (and tested) on their own.
//
// The server owns precedence (approved leave > unexpired manual > online/offline)
// and every label; this file only decides how to *paint* what comes back.
import type { Availability, AvailabilityInput, AvailabilityOption } from './types';

/** The four things a dot can mean. */
export type AvailabilityTone = 'available' | 'offline' | 'manual' | 'leave';

/** Theme tokens only — success/warning/info flip with the theme (see tailwind.config). */
export const AVAILABILITY_DOT_CLASS: Record<AvailabilityTone, string> = {
  available: 'bg-success',
  manual: 'bg-warning',
  leave: 'bg-info',
  offline: 'bg-muted-foreground/40',
};

/** What colour this status is. Leave wins over a manual status, which wins over
 *  plain presence — the same order the server resolved it in. */
export function availabilityTone(a?: Availability | null): AvailabilityTone {
  if (!a) return 'offline';
  if (a.source === 'leave') return 'leave';
  if (a.source === 'manual') return 'manual';
  return a.online && a.kind !== 'OFFLINE' ? 'available' : 'offline';
}

export interface AvailabilityTooltipTexts {
  /** e.g. "From approved time off" — appended when the status is leave-derived. */
  fromLeave: string;
}

/** Hover text: "Commuting · Until 10:45 AM" (+ the time-off provenance when it
 *  came from an approved leave record). */
export function availabilityTooltip(
  a: Availability | null | undefined,
  texts: AvailabilityTooltipTexts,
): string {
  if (!a) return '';
  return [a.label, a.detail, a.text, a.source === 'leave' ? texts.fromLeave : '']
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' · ');
}

export interface DurationTexts {
  restOfToday: string;
  minutes: (count: number) => string;
  hour: string;
  hours: (count: number) => string;
}

/** "30 minutes" / "1 hour" / "2 hours" / "the rest of today" (0 or missing). */
export function durationLabel(minutes: number | undefined, texts: DurationTexts): string {
  if (!minutes || minutes <= 0) return texts.restOfToday;
  if (minutes % 60 !== 0) return texts.minutes(minutes);
  const hours = minutes / 60;
  return hours === 1 ? texts.hour : texts.hours(hours);
}

/** The note a person may attach to their status. Longer notes are a paste accident,
 *  not an intent — clamp rather than reject. */
export const AVAILABILITY_NOTE_MAX = 120;

export interface AvailabilityDraft {
  /** Minutes chosen in the dialog; null/undefined = use the option's suggestion. */
  minutes?: number | null;
  note?: string;
}

/** The PUT body for a picked option. `minutes` is always explicit (0 = rest of
 *  today) so an override and the option's own suggestion travel the same way. */
export function buildAvailabilityPayload(
  option: AvailabilityOption,
  draft: AvailabilityDraft = {},
): AvailabilityInput {
  const note = (draft.note ?? '').trim().slice(0, AVAILABILITY_NOTE_MAX);
  const minutes = draft.minutes == null ? option.default_minutes : draft.minutes;
  const payload: AvailabilityInput = { kind: option.kind, minutes: Math.max(0, minutes) };
  if (note) payload.text = note;
  return payload;
}

/** A leave-derived status is not the person's to clear — only the leave record is. */
export function isLeaveDerived(a?: Availability | null): boolean {
  return a?.source === 'leave';
}

/** Something the person set themselves is the only thing "Clear status" can remove. */
export function canClearAvailability(a?: Availability | null): boolean {
  return a?.source === 'manual';
}

/** Index a batch response by user_id (the endpoint promises no order). */
export function indexAvailability(items: Availability[]): Record<string, Availability> {
  const map: Record<string, Availability> = {};
  items.forEach((item) => {
    if (item?.user_id) map[item.user_id] = item;
  });
  return map;
}
